import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Users, Contact, Loader2, Home, Mail, Phone } from 'lucide-react';
import { communityAPI, profileAPI } from '../../services/api';
import { toast } from 'sonner';

function NeighborAvatar({ path, name }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    (async () => {
      try {
        const res = await profileAPI.getPictureBlob(path);
        if (cancelled) return;
        blobUrl = URL.createObjectURL(res.data);
        setSrc(blobUrl);
      } catch { /* fall back to initials */ }
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]);
  if (!src) {
    const initials = (name || '?').split(' ').slice(0, 2).map(s => s[0] || '').join('').toUpperCase();
    return (
      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 text-indigo-700 font-semibold text-sm">
        {initials || <Home className="w-5 h-5" />}
      </div>
    );
  }
  return <img src={src} alt={name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />;
}

export function MCListModal({ open, onOpenChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await communityAPI.getMCMembers();
        if (!cancelled) setMembers(res.data || []);
      } catch {
        if (!cancelled) toast.error('Failed to load committee list');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mc-list-modal" className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-gk-teal" />
            Management Committee
            <Badge variant="outline" className="ml-1">{members.length}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-gk-teal" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">
              No committee members configured yet.
            </p>
          ) : (
            <div className="space-y-2">
              {members.map((m) => (
                <div
                  key={m.id}
                  data-testid={`mc-list-row-${m.id}`}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
                >
                  <div className="w-10 h-10 rounded-full bg-gk-teal/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-gk-teal" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gk-navy truncate">{m.name}</p>
                    <p className="text-xs text-gk-teal font-medium">{m.role}</p>
                    {m.villa_number && (
                      <p className="text-[11px] text-slate-500">Villa {m.villa_number}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NeighborsDirectoryModal({ open, onOpenChange }) {
  const [neighbors, setNeighbors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await communityAPI.getDirectory();
        if (!cancelled) setNeighbors(res.data || []);
      } catch {
        if (!cancelled) toast.error('Failed to load directory');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? neighbors.filter(n =>
        (n.name || '').toLowerCase().includes(q) ||
        (n.villa_number || '').toLowerCase().includes(q)
      )
    : neighbors;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="neighbors-directory-modal" className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Contact className="w-5 h-5 text-gk-teal" />
            Neighbors Directory
            <Badge variant="outline" className="ml-1">{neighbors.length}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="px-1">
          <input
            data-testid="neighbors-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or villa..."
            className="w-full h-9 px-3 text-sm rounded-md border border-slate-300 focus:border-gk-teal focus:outline-none"
          />
        </div>
        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-gk-teal" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">
              {neighbors.length === 0
                ? 'No neighbors have opted into the directory yet. Opt in from your profile!'
                : 'No matches found.'}
            </p>
          ) : (
            <div className="space-y-2">
              {filtered.map((n) => (
                <div
                  key={n.id}
                  data-testid={`neighbor-row-${n.id}`}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
                >
                  {n.profile_picture_path ? (
                    <NeighborAvatar path={n.profile_picture_path} name={n.name} />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Home className="w-5 h-5 text-indigo-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gk-navy truncate">{n.name}</p>
                    {n.villa_number && (
                      <p className="text-xs text-slate-500">Villa {n.villa_number}</p>
                    )}
                    {n.hobbies && n.hobbies.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`neighbor-hobbies-${n.id}`}>
                        {n.hobbies.slice(0, 6).map((h) => (
                          <span key={h} className="text-[10px] px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">{h}</span>
                        ))}
                        {n.hobbies.length > 6 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">+{n.hobbies.length - 6}</span>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {n.phone && (
                        <a
                          href={`tel:${n.phone}`}
                          data-testid={`neighbor-call-${n.id}`}
                          className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded-full"
                        >
                          <Phone className="w-3 h-3" /> {n.phone}
                        </a>
                      )}
                      {n.email && (
                        <a
                          href={`mailto:${n.email}`}
                          data-testid={`neighbor-email-${n.id}`}
                          className="inline-flex items-center gap-1 text-[11px] text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-full"
                        >
                          <Mail className="w-3 h-3" /> Email
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
